import type { HTMLAttributes } from "react";
import type { ChatStatus, UIMessage } from "ai";
import { cn } from "@drpcrd/utils";
import { ChatMessageActions } from "./chat-actions";
import { AgentIndicator } from "./agent-indicator";

type ChatMessageContentProps = HTMLAttributes<HTMLDivElement>;

/**
 * Chat message bubble. Background/text colors switch via parent data-role.
 */
const ChatMessageContent = ({
  children,
  className,
  ...props
}: ChatMessageContentProps) => (
  <div
    className={cn(
      "text-foreground flex flex-col gap-2 overflow-hidden text-sm",
      // Variants based on parent .group[data-role]
      "group-data-[role=user]:bg-primary-50 group-data-[role=user]:text-primary-foreground group-data-[role=user]:rounded-l-xl group-data-[role=user]:rounded-bl-xl group-data-[role=user]:rounded-br-xl group-data-[role=user]:px-2 group-data-[role=user]:py-1.5",
      "group-data-[role=assistant]:bg-secondary group-data-[role=assistant]:text-foreground",
      "group-data-[role=system]:bg-muted group-data-[role=system]:text-foreground",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

/**
 * Chat message wrapper that aligns message bubble based on sender role.
 */
export type MessageProps = HTMLAttributes<HTMLDivElement> & {
  from: UIMessage["role"];
  status: ChatStatus;
  plaintext?: string;
  agentUuid?: string;
  showAgentUuid?: boolean;
};

export const ChatMessage = ({
  className,
  from,
  children,
  plaintext = "",
  status,
  agentUuid,
  showAgentUuid,
  ...props
}: MessageProps) => (
  <div
    data-role={from}
    className={cn(
      "group flex w-full items-end justify-end",
      from !== "user" && "flex-row-reverse",
      "[&>div]:max-w-[80%]",
      className,
    )}
    {...props}
  >
    <div>
      <ChatMessageContent className={className}>{children}</ChatMessageContent>
      {from === "assistant" && (
        <>
          {status === "ready" && <ChatMessageActions content={plaintext} />}
          {showAgentUuid && <AgentIndicator agentUuid={agentUuid} />}
        </>
      )}
    </div>
  </div>
);
